























import Vue from "vue";
import Component from "vue-class-component";
import { CrudService } from "../core/services/crudService";
import { ShopService } from "../core/services/shopService";
import { Settings, SettingsService } from "../core/services/configService";
import { CustomerHelper } from "../crm/helpers/customerHelper";
import { ProgramPackTexts } from "./programPackTexts";
import ProgramPage from "../components/programs/ProgramPage.vue";
import ProgramHero from "../components/programs/ProgramHero.vue";
import ProgramMeta from "../components/programs/ProgramMeta.vue";
import ProgramPanel from "../components/programs/ProgramPanel.vue";
import ProgramColumns from "../components/programs/ProgramColumns.vue";
import ProgramProgress from "../components/programs/ProgramProgress.vue";
import ProgramReports from "../components/programs/ProgramReports.vue";
import ProgramEvolution from "../components/programs/ProgramEvolution.vue";
import FilterPillsRow from "../components/filters/FilterPillsRow.vue";
import CtaButton from "../components/ctaButton/ctaButton.vue";

@Component({
  components: { ProgramPage, ProgramHero, ProgramMeta, ProgramPanel, ProgramColumns, ProgramProgress, ProgramReports, ProgramEvolution, FilterPillsRow, CtaButton },
})
export default class MyProgramPackPage extends Vue {
  enrollment: any = null;
  reports: any[] = [];
  loaded: boolean = false;
  tab: string = "plan";
  hasEvolution: boolean = false;
  errorMsg: string = "";
  texts = ProgramPackTexts;

  get emptyText(): string {
    return this.loaded && this.enrollment === null ? this.$t("This program is not available").toString() : "";
  }

  get tabs(): any[] {
    return [
      { id: "plan", name: this.t("Plan"), show: true },
      { id: "payments", name: this.t("Payments"), show: !!(this.enrollment.payment && this.enrollment.payment.installments.length) },
      { id: "evolution", name: this.t("Your evolution"), show: this.hasEvolution },
      { id: "reports", name: this.t("Reports"), show: this.reports.length > 0 },
    ].filter((tab) => tab.show);
  }

  get done(): number {
    return ProgramPackTexts.done(this.enrollment);
  }

  get sessions(): number {
    return ProgramPackTexts.sessions(this.enrollment);
  }

  get summary(): string {
    return this.t("{0} done · {1} pending").replace("{0}", String(this.done)).replace("{1}", String(this.sessions - this.done));
  }

  get columns(): any[] {
    return this.enrollment.columns.map((col: any) => ({
      name: col.name,
      rows: col.cards
        .filter((k: any) => k.cardType === 0)
        .reduce((rows: any[], k: any) => rows.concat(ProgramPackTexts.sessionRows(this.t, this.enrollment, k)), []),
    }));
  }

  // A by-days program in progress with appointments still to book is booked whole on one day, as when it was bought.
  get canBookDay(): boolean {
    const e = this.enrollment;
    return e.layout === 0 && e.status === 2 && e.columns.some((col: any) => col.cards.some((k: any) => k.cardType === 0 && k.bookings.length < Math.max(1, k.quantity)));
  }

  get items(): string[] {
    return ProgramPackTexts.itemNames(this.enrollment);
  }

  get paymentSummary(): string {
    const p = this.enrollment.payment;
    return this.t("{0} paid of {1}").replace("{0}", ProgramPackTexts.money(p.totalPaid)).replace("{1}", ProgramPackTexts.money(p.totalWithDiscount));
  }

  // One row per installment; what is due can be paid from here (the checkout charges exactly that), the rest is
  // charged to the saved card on its day.
  get paymentColumns(): any[] {
    const p = this.enrollment.payment;
    return [{
      name: "",
      rows: p.installments.map((i: any) => ({
        name: this.t("Installment {0} of {1}").replace("{0}", String(i.number)).replace("{1}", String(p.installments.length)),
        note: ProgramPackTexts.money(i.amount),
        detail: i.covered ? this.t("paid") : this.t(i.overdue ? "overdue since {0}" : "due on {0}").replace("{0}", ProgramPackTexts.date(i.dueDate)),
        action: i.due && p.dueNow > 0 ? { label: this.t("Pay"), pay: true } : null,
      })),
    }];
  }

  // The sale goes to the cart for what is due now, the amount the checkout will charge.
  pay() {
    const p = this.enrollment.payment;
    const sale = { id: p.saleId, title: this.enrollment.name, description: this.enrollment.name, totalWithDiscount: p.totalWithDiscount, totalPaid: p.totalPaid, pending: p.dueNow, customerId: Number(CustomerHelper.getStoredCustomer()) };
    new ShopService().addSaleToCart(sale, null);
    this.$router.push("/checkout");
  }

  t(key: string): string {
    return this.$t(key).toString();
  }

  // The same public cancellation as "Your appointments": the unit goes back to pending and the row offers "Book" again.
  async cancel(action: any) {
    this.errorMsg = "";
    const data = await new CrudService().httpPost(`${Settings.HostName}/api/publicbookings/cancel/${action.bookingId}/${CustomerHelper.getStoredCustomer()}`);
    if (!data || data.ok === false) {
      this.errorMsg = data && data.errorMsg ? data.errorMsg : this.$t("Crash").toString();
      return;
    }
    await this.load();
  }

  async load() {
    const url = `${Settings.HostName}/api/publicprogrampacks/customer/${CustomerHelper.getStoredCustomer()}/${this.$route.params.id}`;
    const data = await new CrudService().httpGet(url);
    this.enrollment = data && data.ok ? data.model : null;
    this.loaded = true;
  }

  async mounted() {
    await this.load();
    if (this.enrollment) {
      new SettingsService().setPageTitle(this.enrollment.name);
      const sent = await new CrudService().httpGet(`${Settings.HostName}/api/publicreports/customer/${CustomerHelper.getStoredCustomer()}`);
      this.reports = sent && sent.ok ? sent.data.filter((r: any) => r.customerProgramPackId === this.enrollment.id) : [];
    }
  }
}
